import axios from "axios";
import { resolveBackendUrl } from "./backendUrl";

const BASE = resolveBackendUrl();

/* Axios client. Auth is carried by HttpOnly cookies (`withCredentials: true`) —
   we no longer read a JWT from localStorage on the browser (2026-07-21 audit
   hardening). Backend still accepts `Authorization: Bearer` for tests and
   API clients, but the SPA never persists a token client-side.

   For state-changing requests (POST/PUT/PATCH/DELETE) we echo the value of
   the non-HttpOnly `csrf_token` cookie in an `X-CSRF-Token` header, matching
   the backend's double-submit CSRF check. */
export const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
  timeout: 30000,
});

const SAFE_METHODS = new Set(["get", "head", "options"]);

function readCookie(name) {
  if (typeof document === "undefined" || !document.cookie) return null;
  const encoded = encodeURIComponent(name);
  const parts = document.cookie.split(";");
  for (const raw of parts) {
    const [k, ...rest] = raw.trim().split("=");
    if (k === name || k === encoded) {
      return decodeURIComponent(rest.join("="));
    }
  }
  return null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) {
      config.headers = { ...(config.headers || {}), "X-CSRF-Token": csrf };
    }
  }
  return config;
});

// FastAPI 422 returns an array of {msg} objects — flatten to a string so React
// never tries to render an object.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Turns any axios error into a clear, user-facing message. Distinguishes a real
// server response (has a detail) from a network/timeout failure (no response),
// so a dropped connection isn't disguised as a vague "something went wrong".
export function getApiErrorMessage(err) {
  if (err?.response) {
    const detail = err.response.data?.detail;
    if (detail != null) return formatApiErrorDetail(detail);
    return `Something went wrong (error ${err.response.status}). Please try again.`;
  }
  if (err?.code === "ECONNABORTED") {
    return "The server took too long to respond. Please check your connection and try again.";
  }
  return "Couldn't reach the server. Please check your internet connection and try again.";
}
